import { useState, useRef, useEffect, useCallback, type KeyboardEvent } from 'react';
import { MessageSquare, X, Send, Minus, Sparkles, RefreshCw } from 'lucide-react';
import { LOGO, BRAND_NAME, CONTACT_EMAIL } from '@/lib/brand';
import { chatbotApi, isFirebaseConfigured } from '../../config.ts';
import { ConnectHumanButton } from '@/components/support/ConnectHumanButton';
import { WaitingScreen } from '@/components/support/WaitingScreen';
import { LiveChatPanel } from '@/components/support/LiveChatPanel';
import { useSupportSession } from '@/hooks/useSupportSession';
import { useOnlineAgentCount, usePresence } from '@/hooks/usePresence';
import { getAgentProfile, getCurrentUser } from '@/services/authService';
import { detectSupportIntent } from '@/services/supportUtils';
import type { AiHistoryItem } from '@/types/support';
import {
  getSiteLanguage,
  subscribeSiteLanguage,
  type SiteLanguageCode,
} from '@/lib/language';

// ---------------------------------------------------------------------------
// Types & constants
// ---------------------------------------------------------------------------

type ChatMessage = {
  from: 'user' | 'bot';
  text: string;
  time: string;
};

const QUICK_PROMPTS: Record<SiteLanguageCode, string[]> = {
  en: ['Our services', 'Pricing & plans'],
  hi: ['हमारी सेवाएँ', 'मूल्य और योजनाएँ'],
};

const nowTime = () =>
  new Date().toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });

const initialGreeting = (lang: SiteLanguageCode = getSiteLanguage()): ChatMessage => ({
  from: 'bot',
  text:
    lang === 'hi'
      ? 'नमस्ते - मैं DevDotCom असिस्टेंट हूँ। आज मैं आपकी कैसे मदद कर सकता हूँ?'
      : "Hi - I'm the DevDotCom Assistant. How can I help today?",
  time: nowTime(),
});

const CHAT_COPY = {
  en: {
    placeholder: 'Ask about services, pricing, or getting started…',
    statusOnline: 'Online - usually replies instantly',
    statusWaiting: 'Waiting for a support agent…',
    statusEnded: 'Conversation ended',
    errorReach: 'Sorry, I could not reach the assistant service right now. Please try again shortly.',
    errorEmpty: 'Sorry, I could not respond right now.',
    connecting: 'Connecting you to a human support agent…',
    humanUnavailable: (email: string) =>
      `Human support is not configured on this site yet. Email us at ${email} or use the Contact page.`,
  },
  hi: {
    placeholder: 'सेवाएँ, मूल्य, या शुरुआत के बारे में पूछें…',
    statusOnline: 'ऑनलाइन - आमतौर पर तुरंत जवाब मिलता है',
    statusWaiting: 'सपोर्ट एजेंट का इंतज़ार…',
    statusEnded: 'बातचीत समाप्त',
    errorReach: 'क्षमा करें, अभी असिस्टेंट सेवा से संपर्क नहीं हो पाया। कृपया थोड़ी देर बाद फिर कोशिश करें।',
    errorEmpty: 'क्षमा करें, अभी जवाब नहीं दे पाया।',
    connecting: 'आपको मानव सपोर्ट एजेंट से जोड़ रहे हैं…',
    humanUnavailable: (email: string) =>
      `इस साइट पर अभी मानव सपोर्ट उपलब्ध नहीं है। हमें ${email} पर ईमेल करें या संपर्क पेज का उपयोग करें।`,
  },
} as const;

function toAiHistory(messages: ChatMessage[]): AiHistoryItem[] {
  return messages
    .filter((m) => m.from === 'user' || m.from === 'bot')
    .map((m) => ({
      role: m.from === 'user' ? 'user' : 'assistant',
      content: m.text,
    }));
}

export function ChatbotWidget() {
  const [open, setOpen] = useState(false);
  const [language, setLanguage] = useState<SiteLanguageCode>(() => getSiteLanguage());
  const [messages, setMessages] = useState<ChatMessage[]>(() => [initialGreeting(getSiteLanguage())]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [unread, setUnread] = useState(0);
  const [showScrollToBottom, setShowScrollToBottom] = useState(false);
  const [agentName, setAgentName] = useState<string | null>(null);

  const inputRef = useRef<HTMLTextAreaElement | null>(null);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const languageRef = useRef(language);
  languageRef.current = language;
  const copy = CHAT_COPY[language];

  useEffect(() => {
    return subscribeSiteLanguage((code) => {
      setLanguage(code);
      // Refresh greeting when chat is still on the first bot message only.
      setMessages((prev) => {
        if (prev.length === 1 && prev[0]?.from === 'bot') {
          return [initialGreeting(code)];
        }
        return prev;
      });
    });
  }, []);

  const {
    mode,
    request,
    error: supportError,
    connecting,
    online,
    connectToHuman,
    cancelWaiting,
    sendHumanMessage,
    resetToAi,
  } = useSupportSession();

  const onlineAgents = useOnlineAgentCount();
  const agentPresence = usePresence(request?.assignedAgent);
  const supportEnabled = isFirebaseConfigured();

  useEffect(() => {
    if (open && inputRef.current && mode === 'ai') inputRef.current.focus();
    if (open) setUnread(0);
  }, [open, mode]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el || mode !== 'ai') return;
    const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
    if (nearBottom || messages[messages.length - 1]?.from === 'user') {
      el.scrollTop = el.scrollHeight;
    }
  }, [messages, open, isLoading, mode]);

  useEffect(() => {
    if (!open && messages.length > 1 && mode === 'ai') {
      setUnread((u) => u + 1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages]);

  useEffect(() => {
    if (!request?.assignedAgent) {
      setAgentName(null);
      return;
    }
    void getAgentProfile(request.assignedAgent).then((a) => setAgentName(a?.name ?? 'Support agent'));
  }, [request?.assignedAgent]);

  useEffect(() => {
    if (mode === 'human' && request?.assignedAgent && agentName) {
      setMessages((m) => {
        const joinText = `${agentName} joined the conversation.`;
        if (m.some((x) => x.text === joinText)) return m;
        return [...m, { from: 'bot', text: joinText, time: nowTime() }];
      });
    }
  }, [mode, request?.assignedAgent, agentName]);

  useEffect(() => {
    const handleKey = (e: globalThis.KeyboardEvent) => {
      if (e.key === 'Escape' && open) setOpen(false);
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [open]);

  const handleScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    setShowScrollToBottom(distanceFromBottom > 120);
  };

  const scrollToBottom = () => {
    const el = scrollRef.current;
    if (el) el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
  };

  const autoGrow = (el: HTMLTextAreaElement | null) => {
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 112)}px`;
  };

  const startHumanSupport = useCallback(async (
    historyOverride?: ChatMessage[],
    options?: { skipConnectingMessage?: boolean },
  ) => {
    const lang = languageRef.current;
    const t = CHAT_COPY[lang];
    if (!supportEnabled) {
      setMessages((m) => [
        ...m,
        {
          from: 'bot',
          text: t.humanUnavailable(CONTACT_EMAIL),
          time: nowTime(),
        },
      ]);
      return;
    }
    // When the AI already returned a handoff reply, don't append a second
    // "Connecting…" system line - historyOverride already has the bot message.
    if (!options?.skipConnectingMessage) {
      setMessages((m) => [
        ...m,
        { from: 'bot', text: t.connecting, time: nowTime() },
      ]);
    }
    await connectToHuman(toAiHistory(historyOverride ?? messages));
  }, [connectToHuman, messages, supportEnabled]);

  const sendMessage = useCallback(async (raw?: string) => {
    const text = (raw ?? input).trim();
    if (!text || isLoading) return;

    if (mode === 'human') {
      setInput('');
      await sendHumanMessage(text);
      return;
    }

    if (mode === 'waiting' || mode === 'ended') return;

    const wantsHuman =
      detectSupportIntent(text) ||
      text.toLowerCase() === 'talk to a human' ||
      text.toLowerCase() === 'connect to human';

    const history = messages;
    const nextMessages = [...history, { from: 'user' as const, text, time: nowTime() }];
    setMessages(nextMessages);
    setInput('');
    requestAnimationFrame(() => autoGrow(inputRef.current));

    if (wantsHuman) {
      await startHumanSupport(nextMessages);
      return;
    }

    setIsLoading(true);
    const lang = languageRef.current;
    const t = CHAT_COPY[lang];

    try {
      const response = await fetch(`${chatbotApi}/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: text,
          language: lang,
          history: history.map((m) => ({ role: m.from === 'user' ? 'user' : 'assistant', content: m.text })),
        }),
      });

      if (!response.ok) throw new Error('Failed to get chatbot response');

      const data = await response.json();
      const replyText = data.reply || t.errorEmpty;
      const withBot = [...nextMessages, { from: 'bot' as const, text: replyText, time: nowTime() }];
      setMessages(withBot);

      // Backend signals human handoff → start frontend Firebase support connection.
      if (data.action === 'connect_human') {
        await startHumanSupport(withBot, { skipConnectingMessage: true });
      }
    } catch {
      setMessages((m) => [
        ...m,
        { from: 'bot', text: t.errorReach, time: nowTime() },
      ]);
    } finally {
      setIsLoading(false);
    }
  }, [input, isLoading, messages, mode, sendHumanMessage, startHumanSupport]);

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      void sendMessage();
    }
  };

  const handleNewChat = () => {
    resetToAi();
    setMessages([initialGreeting(languageRef.current)]);
    setInput('');
    setIsLoading(false);
    setUnread(0);
    setShowScrollToBottom(false);
    setAgentName(null);
    requestAnimationFrame(() => {
      autoGrow(inputRef.current);
      inputRef.current?.focus();
    });
  };

  const showQuickPrompts = messages.length === 1 && !isLoading && mode === 'ai';
  const customerUid = getCurrentUser()?.uid ?? request?.customerId ?? '';
  const quickPrompts = QUICK_PROMPTS[language];

  const statusLine =
    mode === 'waiting'
      ? copy.statusWaiting
      : mode === 'human'
        ? `${agentName || 'Support'} · ${agentPresence?.online ? 'online' : 'away'}`
        : mode === 'ended'
          ? copy.statusEnded
          : copy.statusOnline;

  return (
    <div>
      <div className="fixed right-4 bottom-4 z-50 flex flex-col items-end gap-3 pointer-events-none sm:right-6 sm:bottom-6">
        {/* ------------------------------------------------------------- */}
        {/* Chat panel                                                   */}
        {/* ------------------------------------------------------------- */}
        <div
          ref={panelRef}
          role="dialog"
          aria-modal="true"
          aria-label="DevDotCom Assistant chat"
          className={`flex flex-col fixed inset-0 w-full h-full max-h-full rounded-none border-0 sm:static sm:inset-auto sm:w-[380px] sm:max-w-[92vw] sm:h-[560px] sm:max-h-[75vh] sm:rounded-2xl sm:border sm:border-border bg-background shadow-2xl shadow-black/10 dark:shadow-black/40 overflow-hidden origin-bottom-right transition-all duration-200 ease-out pointer-events-auto ${
            open
              ? 'translate-y-0 scale-100 opacity-100'
              : 'invisible pointer-events-none absolute h-0 w-0 overflow-hidden opacity-0 sm:h-0 sm:w-0'
          }`}
        >
          {/* Header */}
          <div
            className="relative shrink-0 px-4 pb-3 border-b border-border bg-gradient-to-b from-slate-50 to-background dark:from-white/[0.03] dark:to-background"
            style={{ paddingTop: 'max(0.875rem, env(safe-area-inset-top))' }}
          >
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2.5 min-w-0 flex-1">
                <div className="relative shrink-0">
                  <img
                    src={LOGO.withoutBg}
                    alt={BRAND_NAME}
                    className="h-9 w-9 object-contain dark:hidden"
                    width={36}
                    height={36}
                    loading="lazy"
                    decoding="async"
                  />
                  <img
                    src={LOGO.dark}
                    alt=""
                    aria-hidden="true"
                    className="h-9 w-9 object-contain rounded-full ring-1 ring-border hidden dark:block"
                    width={36}
                    height={36}
                    loading="lazy"
                    decoding="async"
                  />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold text-foreground truncate">
                    {mode === 'human' || mode === 'waiting' ? 'DevDotCom Support' : 'DevDotCom Assistant'}
                  </div>
                  <div className="text-xs text-muted-foreground flex items-center gap-1.5 min-w-0">
                    <span
                      className={`h-1.5 w-1.5 shrink-0 rounded-full ${
                        !online
                          ? 'bg-amber-500'
                          : mode === 'waiting'
                            ? 'bg-amber-500 animate-pulse'
                            : mode === 'human' && agentPresence?.online
                              ? 'bg-emerald-500'
                              : mode === 'ended'
                                ? 'bg-slate-400'
                                : 'bg-emerald-500'
                      }`}
                    />
                    <span className="truncate">{!online ? 'Reconnecting…' : statusLine}</span>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-0.5 shrink-0">
                <button
                  onClick={handleNewChat}
                  disabled={(messages.length === 1 && !isLoading && mode === 'ai') || connecting}
                  aria-label="Start new chat"
                  title="Start new chat"
                  className="p-2 rounded-md text-muted-foreground hover:text-foreground hover:bg-slate-900/5 dark:hover:bg-white/10 transition-colors disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent"
                >
                  <RefreshCw className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setOpen(false)}
                  aria-label="Minimize chat"
                  className="p-2 rounded-md text-muted-foreground hover:text-foreground hover:bg-slate-900/5 dark:hover:bg-white/10 transition-colors"
                >
                  <Minus className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setOpen(false)}
                  aria-label="Close chat"
                  className="p-2 rounded-md text-muted-foreground hover:text-foreground hover:bg-slate-900/5 dark:hover:bg-white/10 transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>

          {/* Messages / support panels */}
          <div className="relative flex-1 min-h-0">
            {mode === 'waiting' && request ? (
              <WaitingScreen
                request={request}
                onlineAgents={onlineAgents}
                onCancel={() => void cancelWaiting()}
              />
            ) : mode === 'human' && request && customerUid ? (
              <LiveChatPanel
                requestId={request.requestId}
                currentUid={customerUid}
                peerUid={request.assignedAgent}
                peerName={agentName || 'Support'}
                role="customer"
                compact
                onSend={sendHumanMessage}
              />
            ) : mode === 'ended' ? (
              <div className="h-full flex flex-col items-center justify-center gap-3 px-6 text-center">
                <p className="text-sm text-muted-foreground">This support conversation has ended.</p>
                {supportError && <p className="text-xs text-destructive">{supportError}</p>}
                <button
                  type="button"
                  onClick={handleNewChat}
                  className="text-[12px] font-medium px-3 py-1.5 rounded-full border border-border hover:border-primary hover:text-primary"
                >
                  Start new chat
                </button>
              </div>
            ) : (
              <>
            <div
              ref={scrollRef}
              onScroll={handleScroll}
              className="h-full overflow-y-auto px-4 py-4 space-y-3 scroll-smooth"
            >
              {messages.map((m, i) => (
                <div
                  key={i}
                  className={`flex items-end gap-2 animate-[chat-msg-in_.25s_ease-out] ${m.from === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  {m.from === 'bot' && (
                    <div className="shrink-0 h-6 w-6 rounded-full bg-primary/10 flex items-center justify-center mb-0.5">
                      <Sparkles className="h-3 w-3 text-primary" />
                    </div>
                  )}
                  <div className={`flex flex-col ${m.from === 'user' ? 'max-w-[80%] items-end' : 'max-w-[92%] items-start'}`}>
                    <div
                      className={`px-3.5 py-2.5 text-[13px] leading-relaxed shadow-sm ${
                        m.from === 'user'
                          ? 'bg-foreground text-background rounded-2xl rounded-br-sm'
                          : 'bg-slate-100 dark:bg-white/[0.06] text-foreground rounded-2xl rounded-bl-sm'
                      }`}
                    >
                      <div className="whitespace-pre-wrap break-words">{m.text}</div>
                    </div>
                    <span className="mt-1 px-1 text-[10px] text-muted-foreground/70">{m.time}</span>
                  </div>
                </div>
              ))}

              {isLoading && (
                <div className="flex items-end gap-2 justify-start">
                  <div className="shrink-0 h-6 w-6 rounded-full bg-primary/10 flex items-center justify-center mb-0.5">
                    <Sparkles className="h-3 w-3 text-primary" />
                  </div>
                  <div className="bg-slate-100 dark:bg-white/[0.06] rounded-2xl rounded-bl-sm px-4 py-3 flex items-center gap-1">
                    <span className="typing-dot" />
                    <span className="typing-dot" style={{ animationDelay: '150ms' }} />
                    <span className="typing-dot" style={{ animationDelay: '300ms' }} />
                  </div>
                </div>
              )}

              {supportError && mode === 'ai' && (
                <p className="text-xs text-destructive">{supportError}</p>
              )}

              {showQuickPrompts && (
                <div className="flex flex-col gap-2 pt-1">
                  <div className="flex flex-wrap gap-1.5">
                    {quickPrompts.map((prompt) => (
                      <button
                        key={prompt}
                        onClick={() => void sendMessage(prompt)}
                        className="text-[12px] font-medium px-3 py-1.5 rounded-full border border-border text-foreground hover:border-primary hover:text-primary hover:bg-primary/5 transition-colors"
                      >
                        {prompt}
                      </button>
                    ))}
                  </div>
                  {supportEnabled && (
                    <ConnectHumanButton
                      onClick={() => void startHumanSupport()}
                      loading={connecting}
                      disabled={isLoading}
                      className="w-full justify-center"
                    />
                  )}
                </div>
              )}
            </div>

            {showScrollToBottom && (
              <button
                onClick={scrollToBottom}
                aria-label="Scroll to latest message"
                className="absolute bottom-3 right-3 h-8 w-8 rounded-full bg-background border border-border shadow-md flex items-center justify-center text-muted-foreground hover:text-foreground transition-colors"
              >
                <svg viewBox="0 0 24 24" fill="none" className="h-4 w-4">
                  <path d="M12 5v14m0 0-6-6m6 6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
            )}
              </>
            )}
          </div>

          {/* Input - AI mode only (human mode uses LiveChatPanel input) */}
          {mode === 'ai' && (
          <div
            className="shrink-0 px-3 pt-2 border-t border-border bg-background"
            style={{ paddingBottom: 'max(0.75rem, env(safe-area-inset-bottom))' }}
          >
            {supportEnabled && !showQuickPrompts && (
              <div className="mb-2">
                <ConnectHumanButton
                  onClick={() => void startHumanSupport()}
                  loading={connecting}
                  disabled={isLoading}
                  className="w-full justify-center h-8 text-[12px]"
                />
              </div>
            )}
            <div className="dcw-input-wrap flex items-end gap-2 rounded-xl border border-border transition-colors px-3 py-2 bg-slate-50 dark:bg-white/[0.03]">
              <textarea
                ref={inputRef}
                value={input}
                onChange={(e) => {
                  setInput(e.target.value);
                  autoGrow(e.target);
                }}
                onKeyDown={handleKeyDown}
                rows={1}
                className="dcw-textarea flex-1 resize-none bg-transparent text-[13px] leading-relaxed placeholder:text-muted-foreground max-h-28"
                placeholder={copy.placeholder}
              />
              <button
                onClick={() => void sendMessage()}
                disabled={!input.trim() || isLoading}
                aria-label="Send message"
                className="dcw-btn shrink-0 h-8 w-8 rounded-lg bg-primary text-primary-foreground flex items-center justify-center disabled:opacity-40 disabled:cursor-not-allowed hover:opacity-90 transition-opacity"
              >
                <Send className="w-3.5 h-3.5" />
              </button>
            </div>
            <div className="mt-1.5 text-center text-[10px] text-muted-foreground/70">
              Powered by DevDotCom AI · Press Enter to send
            </div>
          </div>
          )}
        </div>

        {/* ------------------------------------------------------------- */}
        {/* Floating launcher button                                    */}
        {/* ------------------------------------------------------------- */}
        <div className="relative pointer-events-auto">
          <style>{`
            /* Kill any global/browser/forms-plugin blue focus ring on the chat input */
            .dcw-input-wrap, .dcw-textarea, .dcw-btn {
              outline: none !important;
              box-shadow: none !important;
              -webkit-tap-highlight-color: transparent;
            }
            .dcw-input-wrap:focus,
            .dcw-input-wrap:focus-within,
            .dcw-textarea:focus,
            .dcw-textarea:focus-visible {
              outline: none !important;
              box-shadow: none !important;
              border-color: rgb(148 163 184) !important; /* neutral slate-400 */
            }
            .dark .dcw-input-wrap:focus,
            .dark .dcw-input-wrap:focus-within {
              border-color: rgb(100 116 139) !important; /* neutral slate-500 */
            }
            .dcw-btn:focus-visible {
              outline: 2px solid rgba(100, 116, 139, 0.5) !important;
              outline-offset: 2px;
            }

            @keyframes chat-msg-in { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: translateY(0); } }

            .typing-dot{width:6px;height:6px;border-radius:9999px;background:currentColor;color:rgb(100 116 139);display:inline-block;animation:chat-typing 1s infinite ease-in-out}
            .dark .typing-dot{color:rgb(148 163 184)}
            @keyframes chat-typing{0%,60%,100%{transform:translateY(0);opacity:.4}30%{transform:translateY(-3px);opacity:1}}

            .chatbot-particle{position:absolute;border-radius:9999px;background:currentColor;opacity:.95;filter:blur(.1px);pointer-events:none}
            .chatbot-p1{width:6px;height:6px;left:-10px;top:6px;animation:chat-m1 3200ms infinite ease-in-out}
            .chatbot-p2{width:5px;height:5px;right:-8px;top:10px;animation:chat-m2 2800ms infinite ease-in-out}
            .chatbot-p3{width:6px;height:6px;left:12px;bottom:-8px;animation:chat-m3 3600ms infinite ease-in-out}
            .chatbot-p4{width:4px;height:4px;right:10px;bottom:6px;animation:chat-m4 3000ms infinite ease-in-out}
            .chatbot-p5{width:3px;height:3px;left:2px;top:-6px;animation:chat-m5 2500ms infinite ease-in-out}
            .chatbot-p6{width:4px;height:4px;right:2px;top:-4px;animation:chat-m6 3300ms infinite ease-in-out}
            .chatbot-p7{width:5px;height:5px;left:-4px;bottom:10px;animation:chat-m7 2900ms infinite ease-in-out}
            .chatbot-p8{width:3px;height:3px;right:12px;bottom:-4px;animation:chat-m8 3600ms infinite ease-in-out}

            @keyframes chat-m1{0%{transform:translateY(0) scale(1);opacity:.95}50%{transform:translateY(-8px) scale(1.2);opacity:.5}100%{transform:translateY(0) scale(1);opacity:.95}}
            @keyframes chat-m2{0%{transform:translateX(0) scale(1);opacity:.9}50%{transform:translateX(8px) scale(1.15);opacity:.45}100%{transform:translateX(0) scale(1);opacity:.9}}
            @keyframes chat-m3{0%{transform:translateY(0) scale(1);opacity:.95}50%{transform:translateY(6px) scale(.95);opacity:.45}100%{transform:translateY(0) scale(1);opacity:.95}}
            @keyframes chat-m4{0%{transform:translate(0,0) scale(1);opacity:.85}50%{transform:translate(-6px,-6px) scale(.85);opacity:.35}100%{transform:translate(0,0) scale(1);opacity:.85}}
            @keyframes chat-m5{0%{transform:translate(0,0) scale(1);opacity:.9}50%{transform:translate(-4px,6px) scale(.9);opacity:.4}100%{transform:translate(0,0) scale(1);opacity:.9}}
            @keyframes chat-m6{0%{transform:translate(0,0) scale(1);opacity:.9}50%{transform:translate(6px,-6px) scale(1.05);opacity:.45}100%{transform:translate(0,0) scale(1);opacity:.9}}
            @keyframes chat-m7{0%{transform:translateY(0) scale(1);opacity:.9}50%{transform:translateY(-6px) scale(1.05);opacity:.4}100%{transform:translateY(0) scale(1);opacity:.9}}
            @keyframes chat-m8{0%{transform:translate(0,0) scale(1);opacity:.85}50%{transform:translate(6px,6px) scale(.8);opacity:.35}100%{transform:translate(0,0) scale(1);opacity:.85}}

            .chatbot-anim{animation:chat-button-pulse 3200ms infinite cubic-bezier(.4,0,.2,1);transform-origin:center}
            .chatbot-anim:hover{animation-play-state:paused;transform:scale(1.06)}
            @keyframes chat-button-pulse{0%{transform:scale(1)}25%{transform:scale(1.02)}50%{transform:scale(1.06)}75%{transform:scale(1.02)}100%{transform:scale(1)}}

            @media (prefers-reduced-motion: reduce) {
              .chatbot-anim, .chatbot-particle, .typing-dot { animation: none !important; }
            }
          `}</style>

          <div className={`chatbot-fg text-foreground relative ${open ? 'hidden sm:block' : ''}`}>
            {!open && (
              <>
                <span className="chatbot-particle chatbot-p1" />
                <span className="chatbot-particle chatbot-p2" />
                <span className="chatbot-particle chatbot-p3" />
                <span className="chatbot-particle chatbot-p4" />
                <span className="chatbot-particle chatbot-p5" />
                <span className="chatbot-particle chatbot-p6" />
                <span className="chatbot-particle chatbot-p7" />
                <span className="chatbot-particle chatbot-p8" />
              </>
            )}

            <button
              onClick={() => setOpen((s) => !s)}
              aria-label={open ? 'Close chat' : 'Open chat'}
              aria-expanded={open}
              className={`dcw-btn relative flex items-center justify-center w-[52px] h-[52px] sm:w-14 sm:h-14 rounded-full bg-background text-foreground shadow-2xl transition-all duration-200 hover:scale-105 border border-border ${!open ? 'chatbot-anim' : ''}`}
            >
              {open ? (
                <X className="w-5 h-5" />
              ) : (
                <>
                  <img
                    src={LOGO.withoutBg}
                    alt=""
                    className="h-7 w-7 object-contain dark:hidden"
                  />
                  <img
                    src={LOGO.dark}
                    alt=""
                    aria-hidden
                    className="hidden h-7 w-7 object-contain dark:block"
                  />
                </>
              )}

              {!open && unread > 0 && (
                <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-primary text-primary-foreground text-[10px] font-semibold flex items-center justify-center ring-2 ring-background">
                  {unread > 9 ? '9+' : unread}
                </span>
              )}
              {!open && unread === 0 && (
                <MessageSquare
                  className="pointer-events-none absolute right-0 top-0 z-10 h-[15px] w-[15px] -translate-y-1/2 translate-x-1/2 text-foreground"
                  strokeWidth={2.4}
                  aria-hidden
                />
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ChatbotWidget;